import React from "react";
import { Button, Badge, Modal, Form } from "react-bootstrap";
import moment from "moment";

const ProjectDocumentsTab = ({
  // Data
  documents,
  documentsLoading,

  // Modal State
  showUploadModal,
  setShowUploadModal,

  // Form State
  documentTitle,
  setDocumentTitle,
  documentType,
  setDocumentType,
  selectedFiles,

  // Loading State
  uploadingDocuments,

  // Handlers
  handleFileSelect,
  handleUploadDocuments,
  handleDownloadDocument,
  handleDeleteDocument,
}) => {
  return (
    <div className="project-view-fade-in">
      <div className="project-overview-single-card">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="project-view-overview-section-title mb-1">
              Project Documents
            </h3>
            <p className="text-muted mb-0">
              Manage all documents related to this project
            </p>
          </div>
          <Button variant="primary" onClick={() => setShowUploadModal(true)}>
            Upload Documents
          </Button>
        </div>

        {documentsLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading documents...</span>
            </div>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-folder-open fa-3x mb-3"></i>
            <h5>No Documents Found</h5>
            <p className="mb-4">
              No documents have been uploaded for this project yet.
            </p>
            <Button
              variant="outline-primary"
              onClick={() => setShowUploadModal(true)}
            >
              Upload Your First Document
            </Button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="project-view-document-table">
              <thead className="project-view-document-table-header">
                <tr>
                  <th>Document Name</th>
                  <th>Type</th>
                  <th>Version</th>
                  <th>Uploaded By</th>
                  <th>Upload Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="document-table-body">
                {documents.map((doc) => (
                  <tr
                    key={doc.documentId}
                    className="project-view-document-table-row"
                  >
                    <td className="document-name-cell">
                      <div className="document-name">
                        <i className="fas fa-file me-2 text-primary"></i>
                        {doc.documentTitle}
                      </div>
                    </td>
                    <td className="document-type-cell">
                      <Badge bg="secondary" className="document-type-badge">
                        {doc.documentType}
                      </Badge>
                    </td>
                    <td className="document-version-cell">
                      <div className="document-version">
                        v{doc.documentVersion}
                      </div>
                    </td>
                    <td className="document-uploadedby-cell">
                      <div className="document-uploadedby">
                        {doc.createdBy || "Unknown"}
                      </div>
                    </td>
                    <td className="document-date-cell">
                      {doc.createdOnDate
                        ? moment(doc.createdOnDate).format("MMM DD, YYYY")
                        : "-"}
                    </td>
                    <td className="document-actions-cell">
                      <div className="document-actions">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleDownloadDocument(doc)}
                          className="me-2"
                          title="Download Document"
                        >
                          Download
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() =>
                            handleDeleteDocument(
                              doc.documentId,
                              doc.documentTitle,
                            )
                          }
                          title="Delete Document"
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Document Modal */}
      <Modal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Upload Documents</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Document Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter document title"
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Document Type</Form.Label>
              <Form.Select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
              >
                <option value="">-- Select Document Type --</option>
                <option value="Report">Report</option>
                <option value="Invoice">Invoice</option>
                <option value="Contract">Contract</option>
                <option value="Presentation">Presentation</option>
                <option value="PO">PO</option>
                <option value="SOW">SOW</option>
                <option value="Proposal">Proposal</option>
                <option value="WSR">WSR</option>
                <option value="Project Plan">Project Plan</option>
                <option value="Other">Other</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Select Files</Form.Label>
              <Form.Control
                type="file"
                multiple
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar"
              />
              <Form.Text className="text-muted">
                Maximum file size: 25MB per file. Supported formats: PDF, Word,
                Excel, PowerPoint, Images, Text, Archives
              </Form.Text>
            </Form.Group>

            {selectedFiles.length > 0 && (
              <div className="selected-files-list">
                <h6>Selected Files ({selectedFiles.length}):</h6>
                <ul className="list-group">
                  {selectedFiles.map((file, index) => (
                    <li
                      key={index}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <i className="fas fa-file me-2 text-primary"></i>
                        {file.name}
                      </div>
                      <Badge bg="light" text="dark">
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUploadDocuments}
            disabled={selectedFiles.length === 0 || uploadingDocuments}
          >
            {uploadingDocuments ? (
              <>
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Uploading...</span>
                </div>
                Uploading...
              </>
            ) : (
              <>
                <i className="fas fa-upload me-2"></i>
                Upload {selectedFiles.length} File(s)
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProjectDocumentsTab;
